import axios from "axios";
import { useState } from "react";
import styled from "styled-components";
import Button from "../../common/Button";
import Container from "../../common/Container";
import appConfig from "../../config/appConfig";

const Form = styled.form`
  display: grid;
  gap: 1rem;
  max-width: 560px;
`;

const Field = styled.label`
  display: grid;
  gap: 0.35rem;
  font-weight: 500;

  input,
  textarea {
    font: inherit;
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    background: #fff;
  }

  textarea {
    min-height: 160px;
    resize: vertical;
  }
`;

const Honeypot = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const Status = styled.p<{ error?: boolean }>`
  color: ${(p) => (p.error ? "var(--color-error)" : "var(--color-success)")};
  font-weight: 500;
`;

type FormState = {
  name: string;
  email: string;
  subject: string;
  message: string;
  website: string; // honeypot
};

const empty: FormState = {
  name: "",
  email: "",
  subject: "",
  message: "",
  website: "",
};

function Contact() {
  const [form, setForm] = useState<FormState>(empty);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ msg: string; error: boolean } | null>(
    null
  );

  const update =
    (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setSubmitting(true);
    try {
      const base = appConfig.apis.contactBaseUrl;
      if (!base) throw new Error("Contact API URL is not configured");
      await axios.post(
        `https://${base}/${appConfig.apis.sendContact}`,
        form,
        { headers: { "Content-Type": "application/json" } }
      );
      setStatus({ msg: "Thanks — we'll be in touch.", error: false });
      setForm(empty);
    } catch (err) {
      console.error(err);
      setStatus({
        msg: "Something went wrong sending your message. Please try again or email us directly.",
        error: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <h1>Contact us</h1>
      <p>
        Questions about flying with the group, the fly-in, or paramotoring in
        general? Send a note and one of us will get back to you.
      </p>

      <Form onSubmit={onSubmit} noValidate>
        <Field>
          Name
          <input
            type="text"
            required
            value={form.name}
            onChange={update("name")}
            maxLength={200}
          />
        </Field>
        <Field>
          Email
          <input
            type="email"
            required
            value={form.email}
            onChange={update("email")}
            maxLength={200}
          />
        </Field>
        <Field>
          Subject
          <input
            type="text"
            required
            value={form.subject}
            onChange={update("subject")}
            maxLength={200}
          />
        </Field>
        <Field>
          Message
          <textarea
            required
            value={form.message}
            onChange={update("message")}
            maxLength={5000}
          />
        </Field>
        {/* Honeypot — hidden via CSS, NOT via `display:none` (some bots skip
            display:none fields). Real users never fill this. */}
        <Honeypot aria-hidden="true">
          <label>
            Website
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={update("website")}
            />
          </label>
        </Honeypot>

        <div>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Sending…" : "Send message"}
          </Button>
        </div>
        {status && <Status error={status.error}>{status.msg}</Status>}
      </Form>
    </Container>
  );
}

export default Contact;
